import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Star, Camera, MapPin, ThumbsUp, MessageSquare, CalendarDays, UserPlus, UserCheck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/Avatar";
import { AvatarUpload } from "@/components/AvatarUpload";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HelpfulButton } from "@/components/HelpfulButton";
import { PhotoStrip } from "@/components/ReviewPhotos";
import { ProfilePhotos } from "@/components/ProfilePhotos";
import { ProfilePrivacy } from "@/components/ProfilePrivacy";
import { CommentThread } from "@/components/CommentThread";
import { CATEGORY_MAP, fmtDate } from "@/lib/categories";
import { api, errorMessage } from "@/lib/api";
import { WantToVisit } from "@/components/WantToVisit";

function Stat({ icon: Icon, value, label, tid }) {
  return (
    <div className="rounded-2xl bg-[#151C2C] border border-[#26334D] p-4">
      <Icon className="h-4 w-4 text-[#FFB800]" />
      <p data-testid={tid} className="text-2xl font-extrabold mt-1">{value}</p>
      <p className="text-xs text-slate-400">{label}</p>
    </div>
  );
}

const Empty = ({ children, tid }) => <p data-testid={tid} className="text-sm text-slate-500 rounded-2xl border border-dashed border-[#26334D] p-10 text-center">{children}</p>;

export default function TravelerProfile() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get(`/travelers/${id}`).then((r) => setP(r.data)).catch((e) => setError(errorMessage(e))), [id]);
  useEffect(() => { setP(null); load(); }, [load]);

  const follow = async () => {
    try {
      const { data } = await api.post(`/travelers/${p.id}/follow`);
      setP({ ...p, ...data });
      toast.success(data.is_following ? `You're following ${p.name}` : `Unfollowed ${p.name}`);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const visited = useMemo(() => (p?.visited || []).map((v) => ({ ...v, meta: [v.city, fmtDate(v.day)].filter(Boolean).join(" · ") })), [p]);

  if (error) return <main data-testid="profile-error" className="max-w-xl mx-auto px-4 py-24 text-center text-slate-400">{error}</main>;
  if (!p) return <main className="min-h-[60vh]" />;

  return (
    <main data-testid="traveler-profile" className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center gap-5">
        {p.is_me ? <AvatarUpload url={p.avatar_url} name={p.name} onChange={(url) => setP({ ...p, avatar_url: url })} />
          : <Avatar url={p.avatar_url} name={p.name} size="h-20 w-20" text="text-3xl" className="!rounded-3xl" testid="profile-avatar" />}
        <div>
          <h1 data-testid="profile-name" className="text-4xl font-extrabold tracking-tight">{p.name}</h1>
          <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-1"><CalendarDays className="h-4 w-4" /> Exploring since {fmtDate(p.member_since)}</p>
          <p className="text-sm text-slate-300 mt-1"><span data-testid="profile-followers" className="font-bold">{p.followers}</span> followers · <span data-testid="profile-following" className="font-bold">{p.following}</span> following</p>
        </div>
        {!p.is_me && (
          <Button data-testid="profile-follow-button" onClick={follow}
            className={`ml-auto rounded-full font-semibold ${p.is_following ? "bg-transparent border border-[#26334D] text-white hover:bg-[#1E293B]" : "bg-[#FFB800] hover:bg-[#E6A600] text-[#0B0F19]"}`}>
            {p.is_following ? <><UserCheck className="h-4 w-4 mr-1.5" /> Following</> : <><UserPlus className="h-4 w-4 mr-1.5" /> Follow</>}
          </Button>
        )}
      </div>
      {p.locked ? (
        <div data-testid="profile-locked" className="mt-10 rounded-3xl border border-dashed border-[#26334D] p-12 text-center">
          <Lock className="h-8 w-8 mx-auto text-[#FFB800]" />
          <p className="mt-3 font-semibold">This profile is private</p>
          <p className="text-sm text-slate-400 mt-1">Follow {p.name} to see their photos, reviews and places.</p>
        </div>
      ) : <>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={MessageSquare} value={p.stats.reviews} label="Reviews" tid="profile-stat-reviews" />
        <Stat icon={Camera} value={p.stats.photos} label="Photos" tid="profile-stat-photos" />
        <Stat icon={MapPin} value={p.stats.places} label="Places visited" tid="profile-stat-places" />
        <Stat icon={ThumbsUp} value={p.stats.helpful} label="Helpful votes" tid="profile-stat-helpful" />
      </div>
      {p.is_me && <ProfilePrivacy p={p} onSaved={load} />}
      <Tabs defaultValue={search.get("tab") || (search.get("photo") ? "photos" : "liked")} className="mt-10">
        <TabsList className="bg-[#151C2C] border border-[#26334D] rounded-full p-1 flex-wrap h-auto">
          {[["liked", "Want to visit"], ["reviews", "Reviews"], ["photos", "Photos"], ["visited", "Places visited"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`profile-tab-${v}`} className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#0B0F19]">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="liked" className="mt-6">
          <WantToVisit items={p.liked || []} isMe={p.is_me} name={p.name} />
        </TabsContent>
        <TabsContent value="reviews" className="mt-6 space-y-3">
          {p.reviews.length === 0 && <Empty tid="profile-reviews-empty">No reviews yet.</Empty>}
          {p.reviews.map((r) => (
            <div key={r.id} data-testid={`profile-review-${r.id}`} className="rounded-2xl bg-[#151C2C] border border-[#26334D] p-4">
              <div className="flex flex-wrap justify-between gap-2">
                <p className="font-semibold">{r.place_name} {r.city && <span className="text-sm text-slate-500 font-normal">· {r.city}</span>}</p>
                <span className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "fill-[#FFB800] text-[#FFB800]" : "text-slate-600"}`} />)}</span>
              </div>
              {r.text && <p className="text-sm text-slate-300 mt-2">{r.text}</p>}
              <PhotoStrip photos={r.photos} rid={r.id} />
              {r.reply?.text && <p className="mt-2 pl-3 border-l-2 border-[#FFB800] text-sm text-slate-300"><span className="text-[#FFB800] text-xs font-semibold block">Reply from the business</span>{r.reply.text}</p>}
              <HelpfulButton review={r} onVoted={load} />
              <CommentThread kind="review" target={r.id} count={r.comment_count} tid={`profile-${r.id}`} />
            </div>
          ))}
        </TabsContent>
        <TabsContent value="photos" className="mt-6">
          <ProfilePhotos p={p} reload={load} />
        </TabsContent>
        <TabsContent value="visited" className="mt-6 space-y-2">
          {visited.length === 0 && <Empty tid="profile-visited-empty">No places yet.</Empty>}
          {visited.map((v, i) => {
            const cat = CATEGORY_MAP[v.category];
            return (
              <div key={`${v.name}-${v.city}-${v.day}`} data-testid={`profile-visited-${i}`} className="flex items-center gap-3 rounded-2xl bg-[#151C2C] border border-[#26334D] p-3">
                <span className="h-9 w-9 rounded-xl bg-[#00E5A3]/15 text-[#00E5A3] grid place-items-center">{cat ? <cat.icon className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}</span>
                <div className="flex-1 min-w-0"><p className="font-semibold truncate">{v.name}</p><p className="text-xs text-slate-500">{v.meta}</p></div>
                {v.rating && <span className="text-sm text-[#FFB800] flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-current" />{v.rating}</span>}
              </div>
            );
          })}
        </TabsContent>
      </Tabs>
      </>}
    </main>
  );
}
